/**
 * sede.js
 *
 * - Rellena propiedades ocultas del snippet sede-selector segun opcion elegida
 * - Persiste la sede en localStorage entre productos
 * - Espeja la sede como cart attribute (Sede + _sede_handle + _sede_whatsapp)
 * - Bloquea submit del formulario si no hay sede seleccionada
 * - Muestra el info box con direccion + horario + link a page (+ nota de
 *   "modo reserva" si la opcion trae data-reserva, ver snippets/sede-selector.liquid)
 * - Reintenta el aviso a cart.attributes si falla (hasta 3 veces); si se
 *   agotan los intentos, NO se marca como sincronizado — queda bloqueado a
 *   proposito en vez de dejar pasar la compra (ver 'mk:sede-cart-sync-failed'
 *   y assets/checkout-lock.js). Antes del fix del 21-sep-2026, un fallo de
 *   red aca se trataba igual que un exito y dejaba pasar pedidos sin sede.
 *
 * Auto-inicializa. Reactivo a shopify:section:load y shopify:block:select
 * para funcionar en theme editor.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'lasertam_sede';

  function readStored() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); }
    catch (e) { return null; }
  }

  function persist(option) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        value: option.value,
        handle: option.dataset.handle || ''
      }));
    } catch (e) { /* silent */ }
  }

  function syncHiddenFields(root, option) {
    var map = {
      handle:         option ? (option.dataset.handle || '')         : '',
      direccion:      option ? (option.dataset.direccion || '')      : '',
      whatsapp:       option ? (option.dataset.whatsapp || '')       : '',
      'whatsapp-url': option && option.dataset.whatsappClean ? ('https://wa.me/' + option.dataset.whatsappClean) : '',
      horario:        option ? (option.dataset.horario || '')        : '',
      email:          option ? (option.dataset.email || '')          : '',
      'page-url':     option ? (option.dataset.pageUrl || '')        : ''
    };
    Object.keys(map).forEach(function (k) {
      var f = root.querySelector('[data-sede-field="' + k + '"]');
      if (f) f.value = map[k];
    });
  }

  function renderInfo(root, option) {
    var box = root.querySelector('[data-sede-info]');
    if (!box) return;
    if (!option || !option.value) {
      box.hidden = true;
      return;
    }
    var dirEl = box.querySelector('[data-sede-info-direccion]');
    var horEl = box.querySelector('[data-sede-info-horario]');
    var reservaEl = box.querySelector('[data-sede-info-reserva]');
    var linkEl = box.querySelector('[data-sede-info-link]');
    // Sede en modo reserva (ver snippets/sede-selector.liquid): para ahorrar
    // espacio, se oculta direccion y horario y se deja SOLO el aviso de
    // agenda — la sede todavia no esta operativa, esos datos no aportan
    // todavia.
    var isReserva = option.dataset.reserva === 'true' && !!option.dataset.reservaMsg;
    if (dirEl) {
      dirEl.textContent = option.dataset.direccion || '';
      dirEl.hidden = isReserva;
    }
    if (horEl) {
      horEl.textContent = option.dataset.horario || '';
      horEl.hidden = isReserva;
    }
    if (reservaEl) {
      if (isReserva) {
        reservaEl.textContent = option.dataset.reservaMsg;
        reservaEl.hidden = false;
      } else {
        reservaEl.hidden = true;
      }
    }
    if (linkEl) {
      if (option.dataset.pageUrl) {
        linkEl.href = option.dataset.pageUrl;
        linkEl.hidden = false;
      } else {
        linkEl.hidden = true;
      }
    }
    box.hidden = false;
  }

  // MK: dispara un evento propio (con el <select> en el detail) cuando el
  // aviso a cart.attributes termina EXITOSAMENTE — checkout-lock.js lo
  // escucha para saber cuando es seguro destapar "Comprar ahora". Bug real
  // que esto corrige: un cliente que elige sede y de inmediato hace clic en
  // "Comprar ahora" puede llegar al checkout ANTES de que este fetch
  // (asincronico) termine — la propiedad de la LINEA del producto queda bien
  // (eso es sincronico, va en el mismo submit), pero cart.attributes.Sede
  // queda vacio, y el Flow que taggea la orden lee justamente
  // cart.attributes → orden con sede real elegida pero tag "sede:sin-sede".
  // (Si el aviso falla en vez de tener exito, ver syncCartAttributes() mas
  // abajo — dispara 'mk:sede-cart-sync-failed', NO este evento.)
  var SYNC_MAX_ATTEMPTS = 3;
  var SYNC_RETRY_BASE_MS = 900;

  function markSynced(select) {
    select.dataset.sedeSynced = '1';
    select.dataset.sedeSyncFailed = '0';
    document.dispatchEvent(new CustomEvent('mk:sede-cart-synced', { detail: { select: select } }));
  }

  // MK (fix incidente sede vacia): antes, tanto el .then() como el .catch()
  // de este fetch marcaban sedeSynced='1' — es decir, un fallo de RED
  // (timeout, ad-blocker, hiccup del API) quedaba tratado exactamente igual
  // que un exito, y el boton "Comprar ahora" se desbloqueaba igual aunque
  // cart.attributes.Sede jamas haya quedado escrito server-side. Eso es lo
  // que dejo pasar pedidos con "Sede: sin definir" pese a que el cliente SI
  // habia elegido sede.
  //
  // Ahora: reintenta unas veces, y si TODAS fallan, sedeSynced se queda en
  // '0' a proposito (el overlay de checkout-lock.js sigue bloqueando) — la
  // filosofia ya declarada en checkout-lock.js ("mas seguro partir bloqueado
  // de mas que destapar antes de tiempo") ahora tambien aplica cuando el
  // fetch falla, no solo mientras esta pendiente. Ademas se verifica la
  // respuesta real del carrito (cartAfter.attributes.Sede) en vez de
  // conformarse con que el POST no haya tirado un error de red — un fetch
  // puede resolver "exitosamente" (200) sin que el valor haya quedado
  // aplicado si, por ejemplo, el body no se pudo parsear como se esperaba.
  function syncCartAttributes(option, select, attempt) {
    attempt = attempt || 1;
    var body = {
      attributes: {
        'Sede': option.value,
        '_sede_handle': option.dataset.handle || '',
        '_sede_whatsapp': option.dataset.whatsapp || ''
      }
    };
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/update.js respondio ' + response.status);
        return response.json();
      })
      .then(function (cartAfter) {
        var wroteOk = cartAfter && cartAfter.attributes && cartAfter.attributes['Sede'] === option.value;
        if (!wroteOk) throw new Error('cart.attributes.Sede no quedo igual al valor elegido');
        if (select) markSynced(select);
      })
      .catch(function (err) {
        if (attempt < SYNC_MAX_ATTEMPTS) {
          return new Promise(function (resolve) {
            setTimeout(function () {
              resolve(syncCartAttributes(option, select, attempt + 1));
            }, SYNC_RETRY_BASE_MS * attempt);
          });
        }
        // Se agotaron los reintentos: sedeSynced se queda en '0' a proposito.
        if (select) {
          select.dataset.sedeSynced = '0';
          select.dataset.sedeSyncFailed = '1';
          document.dispatchEvent(new CustomEvent('mk:sede-cart-sync-failed', { detail: { select: select } }));
        }
        console.error('sede.js: no se pudo confirmar la sede en el carrito tras ' + SYNC_MAX_ATTEMPTS + ' intentos.', err);
      });
  }

  function updateCartAttributes(option, select) {
    if (!option || !option.value) return;
    if (select) {
      select.dataset.sedeSynced = '0';
      select.dataset.sedeSyncFailed = '0';
      // Aviso sincronico e inmediato de "recien empezo a sincronizar, no esta
      // confirmado todavia". No alcanza con el evento nativo 'change' del
      // <select> para esto: si checkout-lock.js tiene su PROPIO listener de
      // 'change' en el mismo elemento, el orden en que corren dos listeners
      // distintos sobre el mismo evento depende de cual se registro primero
      // (orden de carga de los scripts) — nada garantiza que el de
      // checkout-lock.js corra DESPUES de que esta linea marque
      // sedeSynced='0'. Disparando este evento a mano, en cambio, el orden
      // causal queda garantizado: primero se marca el dataset, recien
      // despues se avisa.
      document.dispatchEvent(new CustomEvent('mk:sede-cart-sync-start', { detail: { select: select } }));
    }
    syncCartAttributes(option, select, 1);
  }

  // Reintento manual (ej. click del cliente sobre el overlay bloqueado
  // despues de un fallo, ver checkout-lock.js) — usa la misma opcion ya
  // elegida en el <select>, sin pedirle que la vuelva a elegir.
  document.addEventListener('mk:sede-retry-sync', function (e) {
    var select = e.detail && e.detail.select;
    if (!select) return;
    var opt = select.options[select.selectedIndex];
    if (!opt || !opt.value) return;
    select.dataset.sedeSynced = '0';
    select.dataset.sedeSyncFailed = '0';
    document.dispatchEvent(new CustomEvent('mk:sede-cart-sync-start', { detail: { select: select } }));
    syncCartAttributes(opt, select, 1);
  });

  function bind(root) {
    var select = root.querySelector('[data-sede-select]');
    if (!select || select.dataset.sedeBound === '1') return;
    select.dataset.sedeBound = '1';

    // Restaurar sede guardada
    var stored = readStored();
    if (stored && stored.value) {
      var match = Array.prototype.find.call(select.options, function (o) {
        return o.value === stored.value;
      });
      if (match) select.value = match.value;
    }

    function apply(persistFlag) {
      var opt = select.options[select.selectedIndex];
      syncHiddenFields(root, opt);
      renderInfo(root, opt);
      if (opt && opt.value) {
        root.classList.remove('sede-selector--invalid');
        var err = root.querySelector('[data-sede-error]');
        if (err) err.hidden = true;
        if (persistFlag) {
          persist(opt);
          updateCartAttributes(opt, select);
        }
      }
    }

    select.addEventListener('change', function () { apply(true); });

    // MK: antes esto llamaba apply(false) — rellenaba el campo oculto de la
    // LINEA del producto (properties[_sede_handle]) pero NO avisaba al
    // carrito (cart.attributes._sede_handle, PUT via updateCartAttributes).
    // Ese aviso solo se disparaba con el evento 'change' del <select>, asi
    // que si la sede venia restaurada de localStorage (cliente que ya la
    // habia elegido antes) el <select> quedaba pre-rellenado SIN que nadie
    // lo "cambiara" — el pedido se completaba bien (la propiedad de la
    // linea si quedaba correcta), pero cart.attributes._sede_handle quedaba
    // vacio, y el Flow "Tag sede en orden" (que lee justamente
    // order.customAttributes / _sede_handle) taggeaba la orden como
    // sede:sin-sede aunque el cliente si hubiese elegido sede. apply(true)
    // trata la restauracion inicial igual que un 'change' real: si hay un
    // valor valido seleccionado, tambien avisa al carrito. Si no hay nada
    // seleccionado (select en blanco) el guard interno de apply() no manda
    // nada, asi que esto no dispara requests de mas en el caso normal.
    apply(true);

    function isValid() {
      if (select.value) {
        root.classList.remove('sede-selector--invalid');
        var err = root.querySelector('[data-sede-error]');
        if (err) err.hidden = true;
        return true;
      }
      root.classList.add('sede-selector--invalid');
      var err2 = root.querySelector('[data-sede-error]');
      if (err2) err2.hidden = false;
      try { select.focus(); } catch (er) {}
      return false;
    }

    // Guard del submit
    var form = select.closest('form');
    if (form && form.dataset.sedeGuard !== '1') {
      form.dataset.sedeGuard = '1';

      // Fallback: si algun dia el 'submit' SI llega a tiempo al <form>
      // (ej. cambios futuros en el framework), esto lo sigue bloqueando.
      form.addEventListener('submit', function (e) {
        if (!isValid()) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }, true);

      // Guard real (hoy): product-form.js llama a los guardias registrados
      // aca de forma sincronica, antes de hacer fetch al carrito — ver nota
      // en assets/product-form.js sobre por que el 'submit' del <form> no
      // alcanza a bloquear a tiempo.
      window.MkFormGuards = window.MkFormGuards || [];
      window.MkFormGuards.push(function (targetForm) {
        if (targetForm !== form) return true;
        return isValid();
      });
    }
  }

  function init() {
    document.querySelectorAll('[data-sede-selector]').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);
})();
